import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Form, DatePicker, Select, Icon, Tooltip } from 'antd';
import moment from 'moment';
import { FiltersWrapper } from './styles';
import { debounce } from "lodash";


const TableFiltersForm = (props) => {
    const { filterNodes, appliedFilters } = props;
    const { form } = props;
    const { getFieldDecorator } = form;
    // Retrieving all dependant field names into single array
    let dependantFields = []
    filterNodes.forEach(node => dependantFields.push(...(node.dependantFields || [])))
    
    // Retrieving all dependant field node objects.
    let dependantFieldNodes = filterNodes.filter(node => dependantFields.includes(node.field))

    // Reference flag for keeping track of initial render.
    const initialRender = useRef(true);

    const applyFilters = (filter = {}, filterNode = {}) => {
        // Retrieving dependant field of changed filter node
        let dependantFields = filterNode.dependantFields || []; 

        // If dependant field exists reseting dependant field to its default value
        if (dependantFields.length > 0) {
            let resetDefaultValues = {}
            props.filterNodes.filter(node => dependantFields.includes(node.field)).forEach(dependantNode => resetDefaultValues[dependantNode.field] = dependantNode.defaultValue);
            form.setFieldsValue(resetDefaultValues);
        }

        if (filterNode.reset_fields?.length) {
            const resetValues = {};
            filterNode.reset_fields.forEach(key => {
                resetValues[key] = null;
                filter[key] = null;
            });
            form.setFieldsValue({ ...form.getFieldsValue(), ...resetValues });
        }        
        
        if (filterNode && Object.keys(filterNode).includes('onchange')) {
            filterNode.onchange(filter)
        }
        else {
            props.handleFiltersChange({ ...form.getFieldsValue(), ...filter });
        }
    };

    const resetFilters = () => {
        const resetFields = {};
        props.filterNodes.forEach(filterNode => {
            switch (filterNode.inputType) {
                case "text":
                    resetFields[filterNode.field] = filterNode.defaultValue;
                    break;
                case "date_range":
                    resetFields[filterNode.field] = filterNode.defaultValue;
                    break;
                case "select":
                    resetFields[filterNode.field] = filterNode.defaultValue;
                    break;
            }
        });
        form.setFieldsValue(resetFields);
        props.handleFiltersChange(form.getFieldsValue());
    };

    const renderNode = (filterNode) => {
        let node = null;
        switch (filterNode.inputType) {
            case "text":
                node = renderText(filterNode);
                break;
            case "date_range":
                node = renderDateRange(filterNode);
                break;
            case "select":
                node = renderSelect(filterNode);
                break;
            default:
                node = null;
                break;
        }
        return node;
    };

    const renderText = (filterNode) => {
        return (
            <Input
                autoComplete='off'
                className="mb-2"
                size="small"
                type="text"
                placeholder={filterNode.placeholder}
                style={{ width: filterNode.width || 150 }}
                onPressEnter={(e) => applyFilters()}
                disabled={filterNode.disabled}
            />
        )
    };

    const debounceWrapper = debounce((val, onSearch) => {
        onSearch(val)
      }, 1000);

    // On change of dependant field default value update the same and call handleFiltersChange
    useEffect(() => {
        if (initialRender.current) {
            initialRender.current = false;
        }
        else {
            let resetDefaultValues = {}
            dependantFieldNodes.map(dependantNode => {
                resetDefaultValues[dependantNode.field] = dependantNode.defaultValue
            })

            if (Object.keys(resetDefaultValues).length > 0) {
                form.setFieldsValue(resetDefaultValues);
                props.handleFiltersChange({ ...form.getFieldsValue() });
            }
        }
    }, [...dependantFieldNodes.map(dependantNode => dependantNode.defaultValue)])

    const renderDateRange = (filterNode) => {
        let startDate = null;
    
        const minAllowedDate = filterNode.disabledDate ? filterNode.disabledDate : moment().subtract(32, 'days');
        const maxAllowedDate = moment();
    
        return (
            <DatePicker.RangePicker
                className="mb-2"
                disabledDate={(date) => {
                    if (!date) return false;
    
                    // Base restrictions
                    if (date.isAfter(maxAllowedDate, 'day') || date.isBefore(minAllowedDate, 'day')) {
                        return true;
                    }
    
                    // Date range limit
                    if (filterNode.enableLastYearDates) {
                        if (startDate) {
                            const limitDays = filterNode.days_limit || 32;
                            const maxEndDate = startDate.clone().add(limitDays - 1, 'days');
                            const maxStartDate = startDate.clone().subtract(limitDays - 1, 'days');
                            return date.isBefore(maxStartDate, 'day') || date.isAfter(maxEndDate, 'day');
                        }
                    }

                    return false;
                }}
                size="small"
                ranges={{
                    'Today': [moment(), moment()],
                    'Yesterday': [moment().subtract(1, 'day'), moment().subtract(1, 'day')],
                    'This Week': [moment().startOf('week'), moment()],
                    'This Month': [moment().startOf('month'), moment()],
                    'Last 7 Days': [moment().subtract(7, 'days'), moment()],
                    'Last 31 Days': [moment().subtract(31, 'days'), moment()]
                }}
                format={filterNode.dateFormat}
                placeholder={filterNode.placeholder}
                style={{ width: filterNode.width || 200 }}
                onCalendarChange={(dates) => {
                    startDate = dates?.[0] || null;
                }}
                onChange={(values) => applyFilters({ [filterNode.field]: values }, filterNode)}
                disabled={filterNode.disabled}
                allowClear={filterNode.preventClear ? false : true}
            />
        )
    };

    const renderSelect = (filterNode) => {
        return (
            <Select
                className="mb-2"
                size="small"
                showSearch={filterNode.showSearch ? true : false}
                onSearch={filterNode.onSearch ? (value) => {if (value.length >= (filterNode.searchMinLength || 1)) debounceWrapper(value, filterNode.onSearch)} : null}
                placeholder={filterNode.placeholder}
                allowClear={filterNode.preventClear ? false : true}
                style={{ width: filterNode.width || 150 }}
                onChange={(value) => applyFilters({ [filterNode.field]: value }, filterNode)}
                disabled={filterNode.disabled}
                filterOption={(input, option) => {
                    return option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }}
            >
                {
                    filterNode?.options?.map(option => (
                        <Select.Option key={option.key} value={option.value}>{option.label}</Select.Option>
                    ))
                }
            </Select>
        )
    };

    return (
        <div>
            <Form>
                <FiltersWrapper>
                    {
                        filterNodes.map((filterNode, index) => (
                            <div key={`node${index}`} className="mr-2">
                                <label>{filterNode.label}:</label>
                                {getFieldDecorator(filterNode.field, {
                                    initialValue: appliedFilters[filterNode.field]
                                })(renderNode(filterNode))}
                            </div>
                        ))
                    }
                    <Tooltip title="Search">
                        <Button className="mb-2" size="small" type="primary" onClick={(e) => applyFilters()}><Icon type="search" /></Button>
                    </Tooltip>
                    {/* {
                        props.downloadable ?
                            <Tooltip title="Download">
                                <Button
                                    className="ml-1 mb-2"
                                    size="small"
                                    onClick={props.onDownload}
                                    loading={props.isDownloading}
                                    disabled={!props.data || !props.data.length}
                                >
                                    <Icon type="download" />
                                </Button>
                            </Tooltip> : null
                    } */}
                    {
                        props.downloadable ?
                            <>
                                <Tooltip title="Download">
                                    <Button
                                        className="ml-1 mb-2"
                                        size="small"
                                        onClick={props.onDownload}
                                        loading={props.isDownloading}
                                        disabled={!props.data || !props.data.length}
                                    >
                                        <Icon type="download" />
                                    </Button>
                                </Tooltip>

                                {props.enableSettlementMappingReport && (
                                    <Tooltip title="Settlement Mapping Report">
                                        <Button
                                            className="ml-1 mb-2"
                                            size="small"
                                            onClick={() => {
                                                console.log("Settlement Mapping button clicked");
                                                if (props.onSettlementMappingDownload) {
                                                    props.onSettlementMappingDownload();
                                                }
                                            }}
                                            loading={props.isSettlementMappingDownloading}
                                            disabled={!props.data || !props.data.length}
                                        >
                                            Generate Consolidated Settlement Mapping Report
                                        </Button>
                                    </Tooltip>
                                )}
                            </>
                            : null
                    }
                    {/* <Button className="mb-2 ml-1" size="small" type="secondary" onClick={resetFilters}>Reset</Button> */}
                </FiltersWrapper>
            </Form>
        </div>
    );
}


const TableFilters = Form.create()(TableFiltersForm);

TableFilters.defaultProps = {
    filterNodes: [],
    data: [],
    handleFiltersChange: () => { }
};

export { TableFilters };